/**
 * Liste des clients : nom complet, email, téléphone, type, statut
 * vérifié, date d'inscription. Recherche full-text côté client.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { listClients } from '@/features/admin/admin-api';

export function AdminClientsPage() {
  const clients = useQuery({ queryKey: ['admin', 'clients'], queryFn: listClients });
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!clients.data) return [];
    const q = search.toLowerCase().trim();
    if (!q) return clients.data;
    return clients.data.filter((c) =>
      [c.firstName, c.lastName, c.email, c.phone, c.clientType ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [clients.data, search]);

  return (
    <DashboardLayout variant="admin" title="Clients">
      <Card>
        <CardHeader>
          <CardTitle>Tous les clients</CardTitle>
          <CardDescription>
            {clients.data ? `${clients.data.length} comptes` : 'Chargement…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Rechercher (nom, email, téléphone…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Email vérifié</TableHead>
                <TableHead>Inscrit le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-gray-500">
                    Aucun client.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      {c.firstName} {c.lastName}
                    </TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{c.phone}</TableCell>
                    <TableCell className="capitalize">
                      {c.clientType ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          c.emailVerified
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }
                      >
                        {c.emailVerified ? 'Oui' : 'Non'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(c.createdAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/admin/clients/${c.id}`}>Voir détails</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
